import type { ReactElement } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "react-router-dom";

import { apiClient } from "../api/client";
import type { CatalogEntry } from "../api/types";
import { EmptyState } from "../components/EmptyState";
import { RoiCalculator } from "../components/RoiCalculator";
import { SuiteBadge } from "../components/SuiteBadge";
import { Testimonials } from "../components/Testimonials";

export function ProductDetailPage(): ReactElement {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  const { data, isLoading, error } = useQuery<CatalogEntry>({
    queryKey: ["product-detail", slug],
    queryFn: async () => {
      const { data } = await apiClient.get<CatalogEntry>(`/api/catalog/${slug}`);
      return data;
    },
    enabled: !!slug,
  });

  if (isLoading) {
    return (
      <div className="product-detail">
        <div className="skeleton-shimmer" style={{ height: 320, borderRadius: 18 }} />
      </div>
    );
  }

  if (error || !data) {
    return (
      <EmptyState
        icon="⚠️"
        title="Продукт не найден"
        message="Возможно, он не входит в ваш invite или slug неверный."
        action={
          <button className="btn-primary" onClick={() => navigate("/catalog")}>
            Вернуться в каталог
          </button>
        }
      />
    );
  }

  const product = data;

  return (
    <article className="product-detail">
      {/* Breadcrumb */}
      <nav className="product-detail__breadcrumb">
        <Link to="/catalog">← Каталог</Link>
      </nav>

      {/* Hero */}
      <header
        className="product-detail__hero"
        style={{
          background: `linear-gradient(135deg, ${product.accent_color}15 0%, ${product.accent_color}05 100%)`,
          borderColor: product.accent_color,
        }}
      >
        <div className="product-detail__hero-content">
          <SuiteBadge suite={product.suite} size="md" />
          <h1 className="product-detail__title">{product.name}</h1>
          <p className="product-detail__tagline" style={{ color: product.accent_color }}>
            {product.tagline}
          </p>
          <p className="product-detail__roi-headline">{product.roi_headline}</p>

          <Link
            to={`/demo/${product.slug}`}
            className="btn-primary product-detail__launch"
            style={{ backgroundColor: product.accent_color }}
          >
            Запустить demo →
          </Link>
        </div>
        <div className="product-detail__hero-image">
          <img src={product.screenshot_url} alt={`${product.name} preview`} />
        </div>
      </header>

      {/* Description full */}
      <section className="product-detail__section">
        <h2>О продукте</h2>
        <p className="product-detail__description">{product.description_full}</p>
      </section>

      {/* ROI metrics */}
      {product.roi_metrics.length > 0 && (
        <section className="product-detail__section">
          <h2>ROI и метрики</h2>
          <div className="product-detail__metrics-grid">
            {product.roi_metrics.map((m) => (
              <div key={m.metric} className="product-detail__metric-card">
                <div
                  className="product-detail__metric-value"
                  style={{ color: product.accent_color }}
                >
                  {m.value}
                </div>
                <div className="product-detail__metric-label">{m.metric}</div>
                {m.comparison && (
                  <div className="product-detail__metric-comparison">{m.comparison}</div>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Cycle 9 STORY-801: Interactive ROI Calculator */}
      <RoiCalculator slug={product.slug} accentColor={product.accent_color} />

      {/* Cycle 9 STORY-703: Product-specific testimonials (only if any) */}
      <Testimonials filterProduct={product.slug} title={`Отзывы по ${product.name}`} limit={3} />

      {/* ICP personas */}
      {product.icp_personas.length > 0 && (
        <section className="product-detail__section">
          <h2>Для кого этот продукт</h2>
          <div className="product-detail__personas">
            {product.icp_personas.map((persona) => (
              <article key={persona.code} className="persona-card">
                <header className="persona-card__header">
                  <span
                    className="persona-card__code"
                    style={{ backgroundColor: product.accent_color }}
                  >
                    {persona.code}
                  </span>
                  <div>
                    <h3 className="persona-card__title">{persona.title}</h3>
                    <p className="persona-card__company">{persona.company_size}</p>
                  </div>
                </header>
                {persona.pain_points.length > 0 && (
                  <ul className="persona-card__pains">
                    <li className="persona-card__pains-label">Какие проблемы решает:</li>
                    {persona.pain_points.map((pain) => (
                      <li key={pain} className="persona-card__pain">
                        — {pain}
                      </li>
                    ))}
                  </ul>
                )}
              </article>
            ))}
          </div>
        </section>
      )}

      {/* Tags + tech */}
      <section className="product-detail__section product-detail__section--meta">
        <div className="product-detail__meta-block">
          <h3>Отрасли</h3>
          <div className="product-detail__chips">
            {product.industry.map((ind) => (
              <span key={ind} className="product-detail__chip">
                {ind}
              </span>
            ))}
          </div>
        </div>

        {product.tags.length > 0 && (
          <div className="product-detail__meta-block">
            <h3>Технологии</h3>
            <div className="product-detail__chips">
              {product.tags.map((t) => (
                <span key={t} className="product-detail__chip product-detail__chip--tech">
                  {t}
                </span>
              ))}
            </div>
          </div>
        )}

        {product.last_reviewed && (
          <div className="product-detail__meta-block">
            <h3>Обновлено</h3>
            <span className="product-detail__chip product-detail__chip--muted">
              {product.last_reviewed}
            </span>
          </div>
        )}
      </section>

      {/* Bottom CTA */}
      <footer className="product-detail__bottom-cta">
        <Link
          to={`/demo/${product.slug}`}
          className="btn-primary"
          style={{ backgroundColor: product.accent_color, padding: "14px 36px", fontSize: 16 }}
        >
          Запустить demo {product.name}
        </Link>
        <Link to="/catalog" className="btn-secondary">
          Другие продукты
        </Link>
      </footer>
    </article>
  );
}
